import { useState } from "react";
import { DotsNine } from "@phosphor-icons/react";
import { Button, Menu, MenuContent, MenuItem, MenuTrigger, cn } from "@ghezaa/ui";
import { Trans, useLingui } from "@lingui/react/macro";
import { applicationUrl } from "../core-url";
import { useApplications, type LauncherApp } from "../session";

/**
 * The switcher (C7): a nine-dot button opening a grid of the applications the person can open, asked of Core each time
 * it opens. Core is among them only for whoever holds a Core permission. `align`: "start" beside a sidebar logo, "end"
 * at the end of a header. `className` sizes the button (the requester frame's is a 44px target on a phone).
 */
export function AppLauncher({ align = "start", className }: { align?: "start" | "end"; className?: string }) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const applications = useApplications(open);

  return (
    <Menu open={open} onOpenChange={setOpen}>
      <MenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t`Applications`} className={className}>
          <DotsNine />
        </Button>
      </MenuTrigger>
      <MenuContent align={align} collisionPadding={16} className="w-[296px] max-w-[calc(100vw-32px)] p-2">
        <AppLauncherGrid
          applications={applications.data}
          state={applications.data ? "ready" : applications.isError ? "error" : "loading"}
        />
      </MenuContent>
    </Menu>
  );
}

export type AppLauncherGridProps = {
  applications: LauncherApp[] | undefined;
  state: "loading" | "error" | "ready";
  /** False outside the switcher's menu (the specimen): plain links instead of menu items. */
  inMenu?: boolean;
};

/** The switcher's grid, apart from its button and query so the specimen can show it with sample applications. */
export function AppLauncherGrid({ applications, state, inMenu = true }: AppLauncherGridProps) {
  return (
    <div>
      <p className="px-2 pb-2 pt-1 text-2xs font-medium uppercase tracking-wider text-ink-3">
        <Trans>Your applications</Trans>
      </p>
      {state === "ready" && applications && applications.length > 0 ? (
        <div className="grid grid-cols-3 gap-1">
          {applications.map((app) => (
            <Tile key={app.code} app={app} inMenu={inMenu} />
          ))}
        </div>
      ) : state === "error" ? (
        <p className="px-2 pb-2 text-xs text-ink-3">
          <Trans>The list could not be loaded; try again in a moment.</Trans>
        </p>
      ) : state === "ready" ? (
        <p className="px-2 pb-2 text-xs text-ink-3">
          <Trans>No applications yet: your manager or IT can give you access.</Trans>
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-1" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex flex-col items-center gap-2 px-1 py-3">
              <div className="size-11 bg-surface-2" />
              <div className="h-3 w-14 bg-surface-2" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Tile({ app, inMenu }: { app: LauncherApp; inMenu: boolean }) {
  const short = app.name.replace(/^Ghezaa\s+/i, "");
  const [first = "", second = ""] = Array.from(short);
  const link = (
    <a
      href={applicationUrl(app.code, app.url)}
      title={app.description}
      aria-current={app.current ? "page" : undefined}
      className={inMenu ? undefined : "flex flex-col items-center gap-2 px-1 py-3 text-center transition-colors duration-150 ease-out-quick hover:bg-surface-2"}
    >
      <span
        aria-hidden
        className={cn(
          "flex size-11 items-center justify-center border bg-canvas font-mono text-sm text-ink",
          app.current ? "border-ink" : "border-line-strong",
        )}
      >
        {first.toUpperCase()}
        {second}
      </span>
      <span className="w-full truncate text-xs text-ink-2">{short}</span>
    </a>
  );
  return inMenu ? (
    <MenuItem asChild className="h-auto flex-col gap-2 px-1 py-3 text-center">
      {link}
    </MenuItem>
  ) : (
    link
  );
}
